/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import ActionModel from 'cio/models/action';
import { ActionInfo } from 'cio/utils/action-info';
import FiltersService, { FilterMessage } from 'cio/services/filters';
import { Channel } from 'cio/utils/metrics/common';
import { sortByCaseInsensitive } from 'cio/utils/sort';

const QUERY_PARAMS = ['campaign', 'action', 'recipient'] as const;

interface DeliveriesFiltersArgs {
  actionType: Channel;
  actionInfo: ActionInfo;
  action: ActionModel;
  messageId: string;
  recipient: string;
  fixed: string;
  drafted: boolean;
  searchBy: 'email' | 'filter';
  searchFiltersByParent?: boolean;
  searchFiltersBySubject?: boolean;
  onSearch: (term: string) => unknown;
  onActionChange: (action: FilterMessage | null) => unknown;
  onMessageChange: (message: unknown) => unknown;
}

type Filters = Partial<Record<(typeof QUERY_PARAMS)[number], boolean>>;

export default class DeliveriesFilters extends Component<DeliveriesFiltersArgs> {
  @service filters!: FiltersService;

  @tracked enabledFilters: Filters = {};

  get searchFiltersByParent() {
    return this.args.searchFiltersByParent ?? true;
  }

  get searchFiltersBySubject() {
    return this.args.searchFiltersBySubject ?? true;
  }

  get showNewsletters() {
    return this.args.actionType === 'email' && !this.args.drafted;
  }

  get showTransactionalMessages() {
    return this.args.actionType === 'email';
  }

  @computed(
    'filters.campaigns.[]',
    'filters.newsletters.[]',
    'filters.transactionalMessages.[]',
    'showNewsletters',
    'showTransactionalMessages',
  )
  get messages() {
    let { campaigns, transactionalMessages, newsletters } = this.filters;
    return sortByCaseInsensitive(
      [
        ...campaigns,
        ...(this.showTransactionalMessages ? transactionalMessages : []),
        ...(this.showNewsletters ? newsletters : []),
      ],
      // @ts-ignore: does this sorting even work? FilterModel has no "label" property. TODO: check
      ['label'],
    );
  }

  @computed('args.actionType', 'args.messageId', 'filters.messages.[]')
  get filteredMessages() {
    let messages = this.filters.messages.filterBy('type', this.args.actionType);

    if (this.args.drafted) {
      messages = messages.filterBy('draftable');
    }

    let { messageId } = this.args;

    if (messageId) {
      let id = messageId.replace(/^(newsletter|transactional)_/, '');
      let key: keyof FilterMessage;

      if (messageId.startsWith('newsletter_')) {
        key = 'newsletter_id';
      } else if (messageId.startsWith('transactional_')) {
        key = 'transactional_message_id';
      } else {
        key = 'campaign_id';
      }

      messages = messages.filterBy(key, id);
    }

    return messages;
  }

  @computed('enabledFilters.recipient', 'args.actionType', 'args.searchBy')
  get showEmailAddressFilter() {
    return this.enabledFilters.recipient && this.args.actionType === 'email' && this.args.searchBy === 'email';
  }

  @computed('args.messageId')
  get showActionFilter() {
    return !this.args.messageId?.startsWith('transactional_');
  }

  get actionFilterSearchField() {
    if (this.searchFiltersByParent && this.searchFiltersBySubject) {
      return 'searchLabel';
    }

    if (this.searchFiltersByParent) {
      return 'parent.name';
    }

    return 'subject';
  }

  @action
  setFilters() {
    let fixed = this.args.fixed?.split(',').without('') ?? [];

    this.enabledFilters = QUERY_PARAMS.reduce((filtersObj, param) => {
      filtersObj[param] = !fixed.includes(param);
      return filtersObj;
    }, {} as Filters);
  }
}
